
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { Button } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import FoClosedCasesColumnRenderer from "./FoClosedCasesColumnRenderer";
import FoCloseStatusContext from "../FoStatusFilters/FoStatusContext";
import FoCloseCaseRepository, {
  GetFoClosedCasesRequestObject,
} from "../../../../../common/repository/FoCloseCasesRepoitory";
import FoClosedCases from "../../../../../models/FoCloseCase";
import Result from "../../../../../common/repository/Result";
import { toast } from "../../../../common/Toast";
import Table from "../../../../app/Table";
import config from "../../../../../common/repository/config";
import LocalStorage from "../../../../../lib/LocalStorage";

const DEFAULT_PAGE_SIZE = 10;

const FoCloseStatusTable = () => {
  const ctx = useContext(FoCloseStatusContext);
  const history = useHistory();

  const { userRole, user, states, district, reportee, claimNumber } =
    ctx.filters;

  const foCloseRepo = useMemo(() => new FoCloseCaseRepository(), []);

  const columnRenderer = useMemo(
    () =>
      new FoClosedCasesColumnRenderer(
        (model: FoClosedCases) => (
          <Button
            size="small"
            icon
            primary
            onClick={() => history.push(`/case/view/${model.getId()}`)}
          >
            Open
          </Button>
        ),
        (_obj: any) => true
      ),
    [history]
  );

  const [foCloseStatus, setFoCloseStatus] = useState<FoClosedCases[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [totalDataCount, setTotalDataCount] = useState(0);

  const pageSizeRef = useRef(DEFAULT_PAGE_SIZE);
  const offsetRef = useRef(0);
  const latestRequestRef = useRef(0);
  const loadingRef = useRef(false);

  const getLocationFilter = useCallback(() => {
    let state = states?.trim() || "";
    let dist = "";

    if (district?.trim()) {
      try {
        const parsed = JSON.parse(district);
        state = parsed.state?.trim() || state;
        dist = parsed.district?.trim() || "";
      } catch {
        dist = district.trim();
      }
    }

    if (!state && !dist) return undefined;

    return {
      ...(state ? { state } : {}),
      ...(dist ? { district: dist } : {}),
    };
  }, [states, district]);

  const createRequestObject = useCallback(
    (limit: number, offset: number): GetFoClosedCasesRequestObject => {
      const request: GetFoClosedCasesRequestObject = {
        limit,
        offset,
      };

      const selectedUser = user?.trim() || "";
      const selectedRole = userRole?.trim() || "";

      if (selectedUser && selectedRole) {
        request.user = {
          id: selectedUser,
          role: selectedRole,
          reportee: reportee || [],
        };
      }

      const location = getLocationFilter();

      if (location) {
        request.location = location;
      }

      if (claimNumber?.trim()) {
        request.claim_number = claimNumber.trim();
      }

      return request;
    },
    [user, userRole, reportee, getLocationFilter, claimNumber]
  );

  const fetchFoCloseStatus = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current) return;

      const requestId = ++latestRequestRef.current;

      if (reset) {
        offsetRef.current = 0;
      }

      loadingRef.current = true;
      setLoading(true);

      try {
        const limit = pageSizeRef.current;
        const offset = offsetRef.current;

        const response = await foCloseRepo.getFoClosedCases(
          createRequestObject(limit, offset)
        );

        if (requestId !== latestRequestRef.current) return;

        if (response instanceof Result.Error) {
          toast.error(
            response.message || "Failed to fetch FO closed cases status"
          );
          return;
        }

        const items = response.data.items || [];

        setFoCloseStatus((previous) =>
          reset ? items : [...previous, ...items]
        );

        setTotalDataCount(response.data.totalItemCount || 0);
        offsetRef.current = offset + items.length;
      } catch (error) {
        if (requestId !== latestRequestRef.current) return;

        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to fetch FO closed cases status"
        );
      } finally {
        if (requestId === latestRequestRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [foCloseRepo, createRequestObject]
  );

  const downloadFoClosedCases = useCallback(async () => {
    try {
      setDownloading(true);

      const request = {
        ...createRequestObject(0, 0),
        type: "closed",
      };

      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);

      if (!token) {
        toast.error("Token is required");
        return;
      }

      const response = await axios.post(
        `${config.apiBaseURL}/case/foCases/downloadExcel`,
        request,
        {
          responseType: "blob",
          headers: {
            "x-access-token": token,
          },
        }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = url;
      link.download = "FO_Closed_Cases.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download sheet"
      );
    } finally {
      setDownloading(false);
    }
  }, [createRequestObject]);

  useEffect(() => {
    latestRequestRef.current++;
    loadingRef.current = false;
    offsetRef.current = 0;
    setFoCloseStatus([]);
    setTotalDataCount(0);
    fetchFoCloseStatus(true);
  }, [fetchFoCloseStatus]);

  const load = useCallback(() => {
    if (loadingRef.current || offsetRef.current >= totalDataCount) return;
    fetchFoCloseStatus(false);
  }, [fetchFoCloseStatus, totalDataCount]);

  const onRowsPerPageChange = useCallback(
    (_page: number, rowsPerPage: number) => {
      pageSizeRef.current = rowsPerPage;
      latestRequestRef.current++;
      loadingRef.current = false;
      offsetRef.current = 0;
      setFoCloseStatus([]);
      setTotalDataCount(0);
      fetchFoCloseStatus(true);
    },
    [fetchFoCloseStatus]
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
        }}
      >
        <Button
          primary
          icon="download"
          content={downloading ? "Downloading..." : "Download Excel"}
          loading={downloading}
          disabled={downloading}
          onClick={downloadFoClosedCases}
        />
      </div>

      <Table
        key={JSON.stringify([
          userRole,
          user,
          states,
          district,
          claimNumber,
          reportee,
        ])}
        renderer={columnRenderer}
        data={foCloseStatus}
        totalDataCount={totalDataCount}
        loading={loading}
        load={load}
        onRowsPerPageChange={onRowsPerPageChange}
        onSearch={() => {}}
        DownloadBtn={false}
        onClear={() => {}}
      />
    </>
  );
};

export default FoCloseStatusTable;
